import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { EapiClient, type EapiEnvironment } from "@/lib/eapi-client";
import { useAuth } from "./useAuth";
import { useUserRole } from "./useUserRole";
import { useTenant } from "./useTenant";

interface EapiAuthContextType {
  /** Always null — token is now managed server-side in the eapi-proxy. Kept for API compatibility. */
  token: null;
  isReady: boolean;
  /** Always null — kept for API compatibility. */
  error: null;
  eapiClient: EapiClient | null;
  environment: EapiEnvironment;
  setEnvironment: (env: EapiEnvironment) => void;
}

const EapiAuthContext = createContext<EapiAuthContextType>({
  token: null,
  isReady: false,
  error: null,
  eapiClient: null,
  environment: "dev",
  setEnvironment: () => {},
});

// Legacy localStorage key — cleared on mount to remove any stale cached secrets.
const LEGACY_CACHE_KEY = "eapi_onramp_token";

// Roles that actually use EAPI. Terminal staff is included so YMS check-in/out
// can fire AYMS events; Onramp token caching keeps the request volume sane.
const EAPI_ROLES = new Set(["admin", "dispatcher", "terminal_staff"]);

export function EapiAuthProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { roles, isLoading: roleLoading } = useUserRole();
  const { eapiEnv } = useTenant();
  const [environment, setEnvironmentState] = useState<EapiEnvironment>(eapiEnv);
  const clientRef = useRef<EapiClient | null>(null);

  // One-time cleanup of legacy localStorage token cache
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Keep environment in sync with tenant context
  useEffect(() => {
    if (eapiEnv !== environment) {
      setEnvironmentState(eapiEnv);
    }
  }, [eapiEnv]); // eslint-disable-line react-hooks/exhaustive-deps

  // Maintain a single EapiClient instance, updating its environment on change
  if (!clientRef.current) {
    clientRef.current = new EapiClient(environment);
  } else {
    clientRef.current.setEnvironment(environment);
  }

  const setEnvironment = useCallback((env: EapiEnvironment) => {
    setEnvironmentState(env);
  }, []);

  const hasEapiRole = roles.length === 0 || roles.some(r => EAPI_ROLES.has(r));
  const isReady = !!user && !roleLoading && hasEapiRole;

  const value = useMemo(
    () => ({
      token: null as null,
      isReady,
      error: null as null,
      eapiClient: clientRef.current,
      environment,
      setEnvironment,
    }),
    [isReady, environment, setEnvironment]
  );

  return (
    <EapiAuthContext.Provider value={value}>
      {children}
    </EapiAuthContext.Provider>
  );
}

export function useEapiAuth() {
  return useContext(EapiAuthContext);
}
